import { useEffect, useRef, useState } from "react";
import "./CroemCardMethodWidget.scss";

interface CroemCardMethodPaymentProps {
  formError?: string;
  btnText?: string;
  onActionCancel?: React.MouseEventHandler<HTMLButtonElement>;
  onTokenReceived: (
    token: string,
    cardOwnerName: string,
    cardOwnerLastName: string,
    cvv: string
  ) => void;
  onError: (error: string) => void;
}

const CROEM_WIDGET_URL =
  process.env.REACT_APP_CROEM_WIDGET_URL ??
  "https://gateway.merchantprocess.net/securecomponent/v2/UIComponent/CreditCard";

const CroemCardMethodPayment = ({
  formError,
  btnText = "Pagar",
  onActionCancel,
  onTokenReceived,
  onError,
}: CroemCardMethodPaymentProps) => {
  const containerRef = useRef<HTMLDivElement>(null);

  const [cvv, setCvv] = useState("");
  const cvvRef = useRef("");

  const splitCardHolderName = (fullName: string) => {
    if (!fullName) return { name: "", lastName: "" };

    const parts = fullName.trim().split(" ");

    return {
      name: parts[0] || "",
      lastName: parts.slice(1).join(" ") || "",
    };
  };

  useEffect(() => {
    (window as any).SaveCreditCard_SuccessCallback = (response: any) => {
      const token = response?.TokenDetails?.AccountToken;
      const cardHolderName = response?.TokenDetails?.CardHolderName;

      if (!token) {
        onError("Token no recibido");
        return;
      }

      if (!cvvRef.current) {
        onError("Debes ingresar el CVV");
        return;
      }

      const { name, lastName } = splitCardHolderName(cardHolderName);

      onTokenReceived(token, name, lastName, cvvRef.current);
    };

    (window as any).SaveCreditCard_FailureCallback = (response: any) => {
      onError(response?.ResponseDescription || "Error procesando tarjeta");
    };

    (window as any).SaveCreditCard_CancelCallback = () => {
      onError("Usuario canceló la operación");
    };

    loadWidget();

    async function loadWidget() {
      try {
        const response = await fetch(
          `${CROEM_WIDGET_URL}`
        );

        const html = await response.text();

        if (!containerRef.current) return;

        const parser = new DOMParser();
        const doc = parser.parseFromString(html, "text/html");

        const scripts = Array.from(doc.querySelectorAll("script"));

        doc.querySelectorAll("script").forEach((s) => s.remove());

        containerRef.current.innerHTML = doc.body.innerHTML;

        await loadScriptsSequentially(scripts);
      } catch (err) {
        onError("Error cargando widget");
      }
    }

    function loadScriptsSequentially(scripts: HTMLScriptElement[]) {
      return scripts.reduce((promise, script) => {
        return promise.then(() => loadScript(script));
      }, Promise.resolve());
    }

    function loadScript(script: HTMLScriptElement) {
      return new Promise<void>((resolve, reject) => {
        const newScript = document.createElement("script");

        if (script.src) {
          newScript.src = script.src;
          newScript.onload = () => resolve();
          newScript.onerror = reject;
        } else {
          newScript.text = script.innerHTML;
          resolve();
        }

        document.body.appendChild(newScript);
      });
    }

    return () => {
      delete (window as any).SaveCreditCard_SuccessCallback;
      delete (window as any).SaveCreditCard_FailureCallback;
      delete (window as any).SaveCreditCard_CancelCallback;
    };
  }, []);

  return (
    <div className="w-full bg-white">
      <div className="w-full px-4">

        <div className="w-full py-4">
          <h1 className="text-[#212121] font-semibold text-xl">
            Datos de tarjeta
          </h1>
        </div>

        <div
          ref={containerRef}
          id="creditcard-container"
          style={{ width: "100%", minHeight: "400px" }}
        >
          <div style={{ textAlign: "center", padding: "40px" }}>
            Cargando formulario de pago...
          </div>
        </div>

        {/* CVV */}
        <div className="mt-4">
          <label className="text-[#212121] font-semibold text-sm">
            CVV2
          </label>

          <input
            type="password"
            inputMode="numeric"
            maxLength={4}
            value={cvv}
            onChange={(e) => {
              setCvv(e.target.value);
              cvvRef.current = e.target.value;
            }}
            placeholder="123"
            className="w-full mt-1 border border-grayForm rounded-md py-2 px-3"
          />
        </div>

        {formError && (
          <div className="mt-4 text-red-600 text-sm">
            {formError}
          </div>
        )}
      </div>

      <div className="bg-white w-full">
        <div className="w-full grid grid-cols-2 gap-4 px-4 py-4">

          <button
            type="button"
            className="border border-[#212121] rounded-lg py-3"
            onClick={onActionCancel}
          >
            Cancelar
          </button>

          <button
            type="button"
            className="bg-[#212121] text-white rounded-lg py-3"
            onClick={() => {
              if (!cvvRef.current) {
                onError("Ingresa el CVV");
                return;
              }

              const btn = document.getElementById(
                "card-ui-component-btn-confirm"
              ) as HTMLButtonElement;

              if (btn) btn.click();
            }}
          >
            {btnText}
          </button>

        </div>
      </div>
    </div>
  );
};

export default CroemCardMethodPayment;